import { Maybe, ZonedDateTime } from '@csp/csp-common-model';
import { isDefined } from '@csp/csp-common-util';
import { orderBy } from 'lodash';
import { ScheduleWindowInterval } from '../model/ScheduleWindowInterval';

const calcIntersection = (
  interval1: ScheduleWindowInterval,
  interval2: ScheduleWindowInterval,
): Maybe<ScheduleWindowInterval> => {
  const leftLimit =
    interval1.windowStart.unixTimeMillis > interval2.windowStart.unixTimeMillis
      ? interval1.windowStart
      : interval2.windowStart;
  const rightLimit =
    (interval1.windowEnd?.unixTimeMillis ?? Number.MAX_VALUE) <
    (interval2.windowEnd?.unixTimeMillis ?? Number.MAX_VALUE)
      ? interval1.windowEnd
      : interval2.windowEnd;

  return leftLimit.unixTimeMillis < (rightLimit?.unixTimeMillis ?? Number.MAX_VALUE)
    ? {
        windowStart: leftLimit,
        windowEnd: rightLimit,
      }
    : undefined;
};

const intersectionOfIntervalLists = (
  intervals1: ScheduleWindowInterval[],
  intervals2: ScheduleWindowInterval[],
): ScheduleWindowInterval[] => {
  const intersectedIntervals: ScheduleWindowInterval[] = [];
  for (const interval1 of intervals1) {
    for (const interval2 of intervals2) {
      const intersection = calcIntersection(interval1, interval2);
      if (intersection) {
        intersectedIntervals.push(intersection);
      }
    }
  }
  return intersectedIntervals;
};

const diffInternal = (
  minuend: ScheduleWindowInterval,
  subtrahends: ScheduleWindowInterval[],
): ScheduleWindowInterval[] => {
  const subtrahendsCopy = [...subtrahends];
  const subtrahend = subtrahendsCopy.pop();

  if (subtrahend) {
    const diffs = difference(minuend, subtrahend);
    return diffs.flatMap(diff => diffInternal(diff, subtrahendsCopy));
  }

  return [minuend];
};

const differenceOfIntervalLists = (
  intervals1: ScheduleWindowInterval[],
  intervals2: ScheduleWindowInterval[],
): ScheduleWindowInterval[] => {
  const firstDiffs = intervals1.flatMap(interval => diffInternal(interval, intervals2));
  const diff = [...firstDiffs];
  return mergeOverlappingAndSort(diff);
};

const difference = (interval1: ScheduleWindowInterval, interval2: ScheduleWindowInterval): ScheduleWindowInterval[] => {
  // Is overlapping
  if (calcIntersection(interval1, interval2)) {
    const interval1StartMillis = interval1.windowStart.unixTimeMillis;
    const interval1EndMillis = interval1.windowEnd?.unixTimeMillis ?? Number.MAX_VALUE;
    const interval2StartMillis = interval2.windowStart?.unixTimeMillis;
    const interval2EndMillis = interval2.windowEnd?.unixTimeMillis ?? Number.MAX_VALUE;

    const leadingIntervalStart = interval1StartMillis < interval2StartMillis ? interval1.windowStart : undefined;
    const leadingIntervalEnd = interval2.windowStart;

    const trailingIntervalStart = interval2EndMillis < interval1EndMillis ? interval2.windowEnd : undefined;
    const trailingIntervalEnd = interval1.windowEnd;

    return [
      leadingIntervalStart ? ScheduleWindowInterval.from(leadingIntervalStart, leadingIntervalEnd) : undefined,
      trailingIntervalStart ? ScheduleWindowInterval.from(trailingIntervalStart, trailingIntervalEnd) : undefined,
    ].filter(isDefined);
  } else {
    // No overlaps
    return [interval1];
  }
};

const mergeOverlappingAndSort = (intervals: ScheduleWindowInterval[]): ScheduleWindowInterval[] => {
  const sortedIntervals = orderBy(intervals, interval => interval.windowStart.unixTimeMillis);
  return sortedIntervals.reduce((acc, curr) => {
    const previous = acc.pop();
    if (previous) {
      const previousWindowEndOrMax = previous.windowEnd?.unixTimeMillis ?? Number.MAX_VALUE;
      const currentWindowEndOrMax = curr.windowEnd?.unixTimeMillis ?? Number.MAX_VALUE;
      const intervals =
        previousWindowEndOrMax >= curr.windowStart.unixTimeMillis
          ? [
              updateWindowEnd(
                previous,
                previousWindowEndOrMax > currentWindowEndOrMax ? previous.windowEnd : curr.windowEnd,
              ),
            ]
          : [previous, curr];
      return [...acc, ...intervals];
    } else {
      return [curr];
    }
  }, [] as ScheduleWindowInterval[]);
};

const symmetricDifference = (
  intervals1: ScheduleWindowInterval[],
  intervals2: ScheduleWindowInterval[],
): ScheduleWindowInterval[] => {
  const firstDiffs = intervals1.flatMap(interval => diffInternal(interval, intervals2));
  const secondDiffs = intervals2.flatMap(interval => diffInternal(interval, intervals1));
  const symmetricDiff = [...firstDiffs, ...secondDiffs];
  return mergeOverlappingAndSort(symmetricDiff);
};

const updateWindowEnd = (interval: ScheduleWindowInterval, windowEnd?: ZonedDateTime): ScheduleWindowInterval => ({
  ...interval,
  windowEnd,
});

const durationMillis = (interval: ScheduleWindowInterval): number => {
  const windowStartMillis = interval.windowStart.unixTimeMillis;
  const windowEndMillis = interval.windowEnd ? interval.windowEnd.unixTimeMillis : Number.MAX_VALUE;
  return windowEndMillis - windowStartMillis;
};

export const ScheduleIntervalService = {
  intersection: calcIntersection,
  difference,
  symmetricDifference,
  intersectionOfIntervalLists,
  union: mergeOverlappingAndSort,
  updateWindowEnd,
  differenceOfIntervalLists,
  durationMillis,
};
